/**
 * Derives the `--brand*` CSS custom properties from a single per-tenant accent
 * color, using plain HSL math (no dependency). The five variables mirror the
 * hand-tuned tokens in styles/tokens.css so the whole UI recolors by swapping
 * them on :root.
 */

export interface BrandPalette {
  brand: string;
  brandHover: string;
  brandSoft: string;
  brandSoftHover: string;
  brandInk: string;
}

/** The product default accent — kept in sync with styles/tokens.css `--brand`. */
export const DEFAULT_BRAND = '#1f47ab';

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return [h, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number;
  let g: number;
  let b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (v: number) =>
    Math.round(clamp(v, 0, 1) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Returns null when the accent is not a valid 6-digit hex color. */
export function brandPalette(accent: string): BrandPalette | null {
  const rgb = hexToRgb(accent);
  if (!rgb) return null;
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  return {
    brand: hslToHex(h, s, l),
    brandHover: hslToHex(h, s, clamp(l - 0.08, 0, 1)),
    brandSoft: hslToHex(h, clamp(s * 0.55, 0, 1), 0.96),
    brandSoftHover: hslToHex(h, clamp(s * 0.55, 0, 1), 0.92),
    brandInk: hslToHex(h, clamp(s + 0.1, 0, 1), clamp(l * 0.45, 0, 0.32)),
  };
}
